/**
 * Revenue by Source Chart Component
 * Shows revenue breakdown by source (bar chart)
 */

import React, { useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { useColorModeValue } from "@chakra-ui/react";
import { useDisplayCurrency } from "../../../../contexts/DisplayCurrencyContext";

const RevenueBySourceChart: React.FC = () => {
  const { formatMoney, revision } = useDisplayCurrency();
  const textColor = useColorModeValue("gray.700", "white");
  const gridColor = useColorModeValue("#E5E7EB", "#374151");
  const axisColor = useColorModeValue("#6B7280", "#9CA3AF");
  const tooltipTheme = useColorModeValue("light", "dark");

  // Mock revenue by source data
  const revenueSources = [
    { source: "Subscriptions", revenue: 99800 },
    { source: "One-time", revenue: 18750 },
    { source: "Broker Fees", revenue: 4200 },
    { source: "Other", revenue: 2000 },
  ];

  const chartOptions: ApexOptions = useMemo(() => ({
    chart: {
      type: "bar",
      height: 350,
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "55%",
        borderRadius: 4,
        dataLabels: {
          position: "top",
        },
      },
    },
    dataLabels: {
      enabled: true,
      offsetY: -20,
      style: {
        fontSize: "12px",
        colors: [axisColor],
        fontFamily: "'IBM Plex Sans', sans-serif",
      },
      formatter: (val: number) => formatMoney(val),
    },
    stroke: {
      show: true,
      width: 2,
      colors: ["transparent"],
    },
    xaxis: {
      categories: revenueSources.map((s) => s.source),
      labels: {
        style: {
          colors: axisColor,
          fontFamily: "'IBM Plex Sans', sans-serif",
        },
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: axisColor,
          fontFamily: "'IBM Plex Sans', sans-serif",
        },
        formatter: (value: number) => formatMoney(value),
      },
    },
    fill: {
      opacity: 1,
      colors: ["#38BBD1", "#3EEAEC", "#06E363", "#10B981"],
    },
    grid: {
      borderColor: gridColor,
      strokeDashArray: 3,
      xaxis: {
        lines: {
          show: false,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    tooltip: {
      theme: tooltipTheme,
      y: {
        formatter: (value: number) => formatMoney(value),
      },
    },
    colors: ["#38BBD1", "#3EEAEC", "#06E363", "#10B981"],
  }), [formatMoney, revision, axisColor, gridColor, tooltipTheme, revenueSources]);

  const chartSeries = [
    {
      name: "Revenue",
      data: revenueSources.map((s) => s.revenue),
    },
  ];

  return (
    <ReactApexChart
      options={chartOptions}
      series={chartSeries}
      type="bar"
      height={350}
    />
  );
};

export default RevenueBySourceChart;

